/**
 * This component maintains Formik state between renders because depending on the
 * breakpoint inside a GridContainer or breakpoint driven component composition,
 * Formik will unmount (lose data), then remount.
 *
 * This component will save form data to session storage and load it on remount.
 *
 * Usage:
 * <FormikPersist storageKey={ uniqueKeyHere }></FormikPersist>
 *
 * @module views/__core/LayerHost
 * @memberof -Common
 */
import React, { useCallback, useEffect } from 'react';

import { Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import * as utils from '@ulta/core/components/FormikWithPersist/FormikWithPersist';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { getStorage, removeStorage, setStorage } from '@ulta/core/utils/storage/storage';

/**
  * Represents a FormikWithPersist component
  *
  * @method
  * @param {FormikWithPersistProps} parentProps - React properties passed from composition
  * @returns FormikWithPersist
  */
export const FormikWithPersist = ( props ) => {
  const { storageKey, children, onChange, isTesting } = props;

  const formikProps = { ...props };
  delete formikProps.storageKey;
  delete formikProps.children;

  // Disable form persistance during testing unless explicitly asked for
  const enablePersistance = isTesting; // storageKey && ( process.env.NODE_ENV !== 'test' || isTesting );

  if( !children ){
    return null;
  }

  return (
    <Formik { ...spreadProps( { ...formikProps } ) } >
      { ( fprops ) => {
        return (
          <>
            { onChange && <FormikObserver onChange={ onChange } /> }
            { enablePersistance && <FormikPersistHandler storageKey={ storageKey } /> }
            { typeof children === 'function' ? children( fprops ) : children }
          </>
        );
      } }
    </Formik>
  );
};

/**
 * Helper component that handles saving/loading form data between renders
 */
export const FormikPersistHandler = ( props ) => {
  const { storageKey } = props;
  const { values, errors, setValues, setErrors } = useFormikContext();

  // Handles events and clearing storage
  const persistMount = useCallback( utils.composePersistMount( { storageKey, values }, { setValues, setErrors } ), [
    storageKey,
    setValues
  ] );

  useEffect( persistMount, [persistMount] );

  // Handles value changes
  useEffect( () => {
    if( !storageKey ){
      return;
    }

    setStorage( { secure: true, key: storageKey, value: { values, errors } } );
  }, [storageKey, values, errors] );

  return null;
};

/**
 * Handles mount/unmount
 * @param {object} data - Arguments
 * @param {string} data.storageKey - Key used to store data in session storage
 * @param {object} methods - Methods
 * @param {function} methods.setValues - Formik setValues
 * @param {function} methods.setErrors - Formik setErrors
 * @returns {function} - Unmount clean up callback
 */
export const composePersistMount = ( data, methods ) => () => {
  const { storageKey } = data || {};
  const { setValues, setErrors } = methods || {};

  if( !storageKey ){
    return;
  }

  const formikData = getStorage( { secure: true, key: storageKey } );

  if( formikData ){
    setValues( formikData.values );
    setErrors( formikData.errors );
  }

  const clear = () => {
    removeStorage( { secure: true, key: storageKey } );
  };

  global.addEventListener( 'beforeunload', clear );

  // This is the un mount function from the useEffect
  return () => {
    global.removeEventListener( 'beforeunload', clear );
  };
};

export const FormikObserver = ( props ) => {
  const context = useFormikContext();
  useEffect( () => {
    props.onChange && props.onChange( context );
  }, [context] );
  return null;
};

/**
  * @typedef FormikWithPersistProps
  * @type {object}
  *
  * @property {string} storageKey This should be a unique key for the form
  * @property {function} children - Children render function, the same that Formik will pass you
  */
export const propTypes = {
  storageKey: PropTypes.string,
  children: PropTypes.any
};

FormikWithPersist.propTypes = propTypes;

